import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { AccountCreationSteps, QUIZ_PATH, SIGNUP_PATH } from 'constants/signup';
import { trackExistingUserRedirect } from 'utils/analyticsUtils';
import { getSignupLocalStorage, setSignupLocalStorage } from 'utils/signupUtils';

/**
 * Redirect users to the signup flow step (page) that they last left off on.
 * Maintains "sticky progress".
 */
export default function useSignupRedirect({ allowedPaths = [], hasRequiredData = true } = {}) {
    const navigate = useNavigate();
    const { pathname: browserPath } = useLocation();
    const { path: localStoragePath, zip } = getSignupLocalStorage();

    useEffect(() => {
        // Redirect from the quiz summary to the account form when navigating to
        // the summary route with an account creation step saved in local storage.
        if (browserPath === `${QUIZ_PATH}/summary` && Object.values(AccountCreationSteps).includes(localStoragePath)) {
            setSignupLocalStorage({ path: AccountCreationSteps.ACCOUNT });
            trackExistingUserRedirect({ destination: AccountCreationSteps.ACCOUNT });
            navigate(AccountCreationSteps.ACCOUNT);
            return;
        }

        // Redirect to the start of the signup flow if any required data is missing.
        if (browserPath !== SIGNUP_PATH && (!hasRequiredData || !localStoragePath || !zip)) {
            setSignupLocalStorage({ path: SIGNUP_PATH });
            trackExistingUserRedirect({ destination: SIGNUP_PATH });
            navigate(SIGNUP_PATH);
            return;
        }

        // Redirect to the step saved in local storage if the route navigated to
        // does not contain the saved step in its allowed paths.
        if (localStoragePath && browserPath !== localStoragePath && !allowedPaths.includes(localStoragePath)) {
            trackExistingUserRedirect({ destination: localStoragePath });
            navigate(localStoragePath);
        }
    }, []);
}
